import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon, X } from "lucide-react";

export function PhotoUpload({ bookingId, onUploaded, existing = [], compact = false }) {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(existing);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    let uploaded = 0;
    for (const f of Array.from(files).slice(0, 5)) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error(`${f.name}: formato non supportato`);
        continue;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: troppo grande (max 5MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/bookings/${bookingId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((prev) => [...prev, { photo_id: data.photo_id }]);
        uploaded++;
      } catch (e) {
        toast.error(`${f.name}: errore upload`);
      }
    }
    setBusy(false);
    if (uploaded > 0) {
      toast.success(`${uploaded} foto caricate`);
      onUploaded?.();
    }
  };

  return (
    <div className={compact ? "" : "mt-4"}>
      {!compact && <div className="text-sm font-medium mb-2">Foto (facoltative, max 5)</div>}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => upload(e.target.files)}
        data-testid="photo-upload-input"
      />
      <div className="flex items-center gap-3 flex-wrap">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-full" data-testid="photo-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Upload className="h-4 w-4 mr-2"/>}
          {busy ? "Caricamento..." : "Aggiungi foto"}
        </Button>
        {items.map((p) => (
          <div key={p.photo_id} className="h-14 w-14 rounded-md border border-border bg-secondary flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-foreground/50" />
          </div>
        ))}
      </div>
      <div className="text-xs text-foreground/50 mt-2">JPG · PNG · WEBP · max 5MB ciascuna</div>
    </div>
  );
}

export function PhotoGallery({ bookingId, photos }) {
  const token = localStorage.getItem("maurizi_token");
  const [zoom, setZoom] = useState(null);
  if (!photos || photos.length === 0) return null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  return (
    <div>
      <div className="flex gap-2 flex-wrap mt-2">
        {photos.map((p) => {
          const src = `${backend}/api/photos/${p.photo_id}?auth=${token}`;
          return (
            <button key={p.photo_id} type="button" onClick={() => setZoom(src)} className="h-16 w-16 rounded-md border border-border overflow-hidden bg-secondary" data-testid={`photo-thumb-${p.photo_id}`}>
              <img src={src} alt="" className="h-full w-full object-cover" />
            </button>
          );
        })}
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setZoom(null)}>
          <button onClick={() => setZoom(null)} className="absolute top-6 right-6 text-white p-2"><X className="h-6 w-6"/></button>
          <img src={zoom} alt="" className="max-h-full max-w-full rounded" />
        </div>
      )}
    </div>
  );
}
